export type OnboardingStatusAPIType = {
  slug: OnboardingSteps;
  label: string;
};

// Shape returned by GET /api/vendor-onboarding/status
// (VendorOnboardingStatusResource on the BE). The gate reads
// `onboarding.onboarding_status` (the vendor-level `on_boarding_status` flag),
// `onboarding.signed_url_params` (regenerated resume link), and
// `profile.user_type`. `.unwrap()` resolves to `any`, so callers cast to this
// type rather than introducing `any`.
export type VendorOnboardingStatusResponse = {
  onboarding: {
    vendor_id?: string;
    onboarding_id?: string;
    onboarding_step?: {
      slug: string;
      label: string;
    };
    onboarding_status: 'pending' | 'complete';
    signed_url_params: string;
    // Server-side once-only record: true once the approved-vendor welcome banner
    // has been shown, so it appears only on the first dashboard visit (ORI-677).
    welcome_banner_seen?: boolean;
  };
  profile: {
    first_name?: string;
    last_name?: string;
    user_type: string;
  };
};

export type OnboardingDataAPIType = {
  vendor_onboarding_uuid: string;
  'basic-info': {
    vendor_uuid: string;
    email: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    company_name: string;
    service: string;
    address1: string;
    address2: string;
    city: string;
    // StateResource::collection on the wire — an array with one entry, or empty
    // when the vendor's stored state code resolves to no known state.
    state:
      | Array<{
          value: string;
          label: string;
        }>
      | {
          value: string;
          label: string;
        };
    postal_zip_code: string;
    gl_insurance_expire_at: string;
    is_synced: boolean;
    // True when the vendor has agreed to receive texts at the company phone number.
    agree_to_texts?: boolean;
  };
  'service-offered'?: ServiceOfferedDataType;
  'service-area'?: ServiceAreaDataType;
  type: string;
  'location-api-key'?: string;
  status: OnboardingStatusAPIType;
};

export type OnboardingStepsType<T> = {
  BASIC_INFO: T;
  SET_PASSWORD: T;
  SERVICE_AREA: T;
  SERVICES_OFFERED: T;
  UNDER_REVIEW: T;
};

export type OnboardingPageType<T> = OnboardingStepsType<T> & {
  EXPIRED: T;
  INITIAL: T;
};

export const ONBOARDING_STEPS = {
  BASIC_INFO: 'basic-info',
  INITIAL: 'initial',
  SERVICE_AREA: 'service-area',
  SERVICES_OFFERED: 'service-offered',
  SET_PASSWORD: 'set-password',
  UNDER_REVIEW: 'under-review',
} as const;

export type OnboardingSteps = (typeof ONBOARDING_STEPS)[keyof typeof ONBOARDING_STEPS];

export type OnboardingUserInfo = {
  address1: string;
  address2?: string;
  city: string;
  company?: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  service: string;
  // Normalized by the reducers to the single selected option (the API sends a
  // StateResource collection).
  state?: { value: string; label: string };
  vendorId: string;
  zip: string;
  insuranceExpiry?: string | null;
  // True when this vendor record originates from a property management system
  // (Yardi/AppFolio). Fields the PMS actually populated are locked in onboarding.
  isSynced: boolean;
  // True when the vendor has agreed to receive texts at the company phone number.
  agreeToTexts?: boolean;
};

export type OnboardingSignedUrlType = {
  companyName: string;
  data: string;
  token: string;
  expires: string;
  signature: string;
};

export type OnboardingStateType = {
  basicInfo: OnboardingUserInfo;
  isLoading: boolean;
  onboardingId: string;
  serviceArea?: ServiceAreaDataType;
  serviceOffered?: ServiceOfferedDataType;
  signedUrl?: OnboardingSignedUrlType;
  type: string;
  step: OnboardingSteps;
  locationAPIKey?: string;
};

export type ServiceOfferedDataType = {
  selected_skills?: {
    selected_diagnosis_ids?: string[];
    selected_problem_category_ids?: string[];
    selected_sub_category_ids?: string[];
  };
};

export type VendorSkillMapping = Array<{
  label: string;
  value: string;
  is_selected?: boolean;
  slug: string;
  sub_categories?: VendorSkillMapping;
  diagnosis?: VendorSkillMapping;
}>;

export type VendorSkillMappingPayload = Array<{
  problem_category_id?: string;
  problem_sub_category_id?: string;
  is_selected?: boolean;
  sub_categories?: {
    sub_category_id: string;
    is_selected?: boolean;
    diagnosis?: {
      is_selected?: boolean;
    }[];
  }[];
}>;

export type ServiceOfferedCurrentState = 'indeterminate' | 'checked' | '';

export type SearchAddressDataType = {
  label: string;
  geometry: {
    point: Array<number>;
  };
  municipality: string;
  sub_region: string;
  state: string;
  country: string;
  postal_code: string;
};

export type ServiceAreaDataType = {
  vendor_id: string;
  zip_codes: string[];
  radius: string;
};
